<template>
  <Container>
    <template #header>
      <HeaderFe />
    </template>
    <template v-slot:default>
      <main class="pt-28">
        <section class="pb-10">
          <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0">
            <h2 class="mt-8 mb-6 font-semibold text-heading-5 text-dark">
              Grafik Data Mahasiswa
            </h2>

            <!-- Filter Section -->
            <div class="p-6 mb-8 bg-white rounded-lg shadow-md">
              <h3 class="mb-4 text-lg font-semibold">Filter Data</h3>
              <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label class="block mb-2 text-sm font-medium text-gray-700">
                    Tahun Akademik
                  </label>
                  <select
                    v-model="selectedTahunAkademik"
                    @change="updateChart"
                    :disabled="isLoading"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <option value="">Semua Tahun</option>
                    <option
                      v-for="tahun in tahunAkademikList"
                      :key="tahun"
                      :value="tahun"
                    >
                      {{ tahun }}
                    </option>
                  </select>
                </div>

                <div>
                  <label class="block mb-2 text-sm font-medium text-gray-700">
                    Semester
                  </label>
                  <select
                    v-model="selectedSemester"
                    @change="updateChart"
                    :disabled="isLoading"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <option value="">Semua Semester</option>
                    <option value="ganjil">Ganjil</option>
                    <option value="genap">Genap</option>
                  </select>
                </div>

                <div>
                  <label class="block mb-2 text-sm font-medium text-gray-700">
                    Program Studi
                  </label>
                  <select
                    v-model="selectedProdi"
                    @change="updateChart"
                    :disabled="isLoading"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <option value="">Semua Program Studi</option>
                    <option
                      v-for="prodi in prodiList.data"
                      :key="prodi.kodeprodi"
                      :value="Number(prodi.kodeprodi)"
                    >
                      {{ prodi.jenjang }} - {{ prodi.name }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Loading indicator -->
              <div v-if="isLoading" class="mt-4 text-center">
                <div class="inline-flex items-center text-blue-600">
                  <svg class="w-4 h-4 mr-2 animate-spin" viewBox="0 0 24 24">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                      fill="none"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Memuat data...
                </div>
              </div>
            </div>

            <!-- Filter Information -->
            <div class="p-4 mb-8 border border-gray-200 rounded-lg bg-gray-50">
              <h3 class="mb-2 text-base font-medium text-gray-800">
                Informasi Filter:
              </h3>
              <div class="grid grid-cols-1 gap-2 md:grid-cols-3">
                <div class="flex items-center">
                  <span class="mr-2 text-sm font-semibold text-gray-600"
                    >Tahun Akademik:</span
                  >
                  <span class="text-sm text-gray-800">{{
                    selectedTahunAkademik || "Semua Tahun"
                  }}</span>
                </div>
                <div class="flex items-center">
                  <span class="mr-2 text-sm font-semibold text-gray-600"
                    >Semester:</span
                  >
                  <span class="text-sm text-gray-800">{{
                    selectedSemester
                      ? selectedSemester.charAt(0).toUpperCase() +
                        selectedSemester.slice(1)
                      : "Semua Semester"
                  }}</span>
                </div>
                <div class="flex items-center">
                  <span class="mr-2 text-sm font-semibold text-gray-600"
                    >Program Studi:</span
                  >
                  <span class="text-sm text-gray-800">{{
                    selectedProdi
                      ? getSelectedProdiName() || `Prodi ${selectedProdi}`
                      : "Semua Program Studi"
                  }}</span>
                </div>
              </div>
            </div>

            <!-- Statistics Cards -->
            <div class="grid grid-cols-1 gap-6 mb-8 md:grid-cols-4">
              <div
                class="p-6 text-white rounded-lg bg-gradient-to-r from-blue-500 to-blue-600"
              >
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-sm font-medium text-blue-100">
                      Total Mahasiswa
                    </p>
                    <p class="text-2xl font-bold">
                      {{ totalMahasiswa.toLocaleString() }}
                    </p>
                  </div>
                  <div class="p-3 bg-white rounded-lg bg-opacity-20">
                    <svg
                      class="w-8 h-8"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg>
                  </div>
                </div>
              </div>

              <div
                class="p-6 text-white rounded-lg bg-gradient-to-r from-blue-600 to-blue-700"
              >
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-sm font-medium text-blue-100">
                      Semester Ganjil / Genap
                    </p>
                    <div class="flex items-baseline gap-2">
                      <span class="text-2xl font-bold">{{
                        totalGanjil.toLocaleString()
                      }}</span>
                      <span class="text-sm text-blue-200">/</span>
                      <span class="text-2xl font-bold">{{
                        totalGenap.toLocaleString()
                      }}</span>
                    </div>
                    <div class="flex items-center mt-1 text-xs text-blue-100">
                      <span class="font-medium">{{ ganjilPercent }}%</span>
                      <span class="mx-1">/</span>
                      <span class="font-medium">{{ genapPercent }}%</span>
                    </div>
                  </div>
                  <div class="p-3 bg-white rounded-lg bg-opacity-20">
                    <svg
                      class="w-8 h-8"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"
                      ></path>
                    </svg>
                  </div>
                </div>
              </div>

              <div
                class="p-6 text-white rounded-lg bg-gradient-to-r from-green-500 to-green-600"
              >
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-sm font-medium text-green-100">
                      Total Program Studi
                    </p>
                    <p class="text-2xl font-bold">{{ totalProdi }}</p>
                  </div>
                  <div class="p-3 bg-white rounded-lg bg-opacity-20">
                    <svg
                      class="w-8 h-8"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"
                      ></path>
                    </svg>
                  </div>
                </div>
              </div>

              <div
                class="p-6 text-white rounded-lg bg-gradient-to-r from-purple-500 to-purple-600"
              >
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-sm font-medium text-purple-100">
                      Rata-rata per Prodi
                    </p>
                    <p class="text-2xl font-bold">
                      {{ averagePerProdi.toLocaleString() }}
                    </p>
                  </div>
                  <div class="p-3 bg-white rounded-lg bg-opacity-20">
                    <svg
                      class="w-8 h-8"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z"
                        clip-rule="evenodd"
                      ></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <!-- Chart Section -->
            <div class="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2">
              <!-- Pie Chart -->
              <div class="p-6 bg-white rounded-lg shadow-md">
                <h3 class="mb-4 text-lg font-semibold">
                  {{
                    selectedProdi
                      ? `Grafik Mahasiswa ${
                          getSelectedProdiName() || `Prodi ${selectedProdi}`
                        }`
                      : "Grafik Distribusi Mahasiswa per Program Studi"
                  }}
                </h3>
                <div class="h-80">
                  <canvas ref="pieChart"></canvas>
                </div>
              </div>

              <!-- Semester Distribution Chart -->
              <div class="p-6 bg-white rounded-lg shadow-md">
                <h3 class="mb-4 text-lg font-semibold">
                  Distribusi Mahasiswa per Semester
                </h3>
                <div class="h-80">
                  <canvas ref="semesterChart"></canvas>
                </div>
              </div>
            </div>

            <!-- Data Table -->
            <div class="p-6 mb-6 bg-white rounded-lg shadow-md">
              <h4 class="mb-3 text-base font-medium text-gray-800">
                Ringkasan Distribusi Mahasiswa
              </h4>

              <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div class="p-4 border border-gray-100 rounded-lg bg-gray-50">
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-sm font-medium text-gray-700"
                      >Distribusi per Semester</span
                    >
                    <span class="text-xs font-medium text-gray-500"
                      >Total:
                      {{ totalMahasiswa.toLocaleString() }} mahasiswa</span
                    >
                  </div>

                  <div class="flex items-center mb-4">
                    <div
                      class="relative w-full overflow-hidden bg-gray-100 rounded-full h-7"
                    >
                      <div
                        class="absolute top-0 left-0 h-full bg-blue-500 rounded-l-full"
                        :style="{ width: `${ganjilPercent}%` }"
                      ></div>
                      <div
                        class="absolute top-0 right-0 h-full bg-green-500 rounded-r-full"
                        :style="{ width: `${genapPercent}%` }"
                      ></div>

                      <!-- Percentage labels on the bars -->
                      <div
                        class="absolute inset-0 flex items-center justify-between px-3"
                      >
                        <span
                          v-if="ganjilPercent > 15"
                          class="z-10 text-xs font-medium text-white"
                          >{{ ganjilPercent }}%</span
                        >
                        <span
                          v-if="genapPercent > 15"
                          class="z-10 text-xs font-medium text-white"
                          >{{ genapPercent }}%</span
                        >
                      </div>
                    </div>
                  </div>

                  <div class="flex justify-between text-sm">
                    <div class="flex items-center">
                      <span
                        class="inline-block w-3 h-3 mr-2 bg-blue-500 rounded-full"
                      ></span>
                      <div>
                        <span class="font-medium">Semester Ganjil</span>
                        <div class="text-xs text-gray-600">
                          {{ totalGanjil.toLocaleString() }} mahasiswa
                        </div>
                      </div>
                    </div>

                    <div class="flex items-center">
                      <span
                        class="inline-block w-3 h-3 mr-2 bg-green-500 rounded-full"
                      ></span>
                      <div>
                        <span class="font-medium">Semester Genap</span>
                        <div class="text-xs text-gray-600">
                          {{ totalGenap.toLocaleString() }} mahasiswa
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="p-4 border border-gray-100 rounded-lg bg-gray-50">
                  <div class="mb-2">
                    <span class="text-sm font-medium text-gray-700"
                      >Statistik Program Studi</span
                    >
                  </div>

                  <div class="grid grid-cols-2 gap-3">
                    <div class="p-3 bg-white border border-gray-100 rounded-lg">
                      <div class="text-xl font-bold text-blue-600">
                        {{ totalProdi }}
                      </div>
                      <div class="text-xs text-gray-500">
                        Total Program Studi
                      </div>
                    </div>

                    <div class="p-3 bg-white border border-gray-100 rounded-lg">
                      <div class="text-xl font-bold text-green-600">
                        {{ averagePerProdi.toLocaleString() }}
                      </div>
                      <div class="text-xs text-gray-500">
                        Rata-rata Mahasiswa
                      </div>
                    </div>

                    <div class="p-3 bg-white border border-gray-100 rounded-lg">
                      <div class="text-xl font-bold text-purple-600">
                        {{
                          processedChartData && processedChartData.length > 0
                            ? processedChartData[0].total_mahasiswa.toLocaleString()
                            : 0
                        }}
                      </div>
                      <div class="text-xs text-gray-500">Prodi Terbanyak</div>
                    </div>

                    <div class="p-3 bg-white border border-gray-100 rounded-lg">
                      <div class="text-xl font-bold text-amber-600">
                        {{
                          selectedSemester === "ganjil"
                            ? "Ganjil"
                            : selectedSemester === "genap"
                            ? "Genap"
                            : totalGanjil > totalGenap
                            ? "Ganjil"
                            : "Genap"
                        }}
                      </div>
                      <div class="text-xs text-gray-500">Semester Dominan</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Data Table Detail -->
            <div class="overflow-hidden bg-white rounded-lg shadow-md">
              <div class="px-6 py-4 border-b border-gray-200">
                <h3 class="text-lg font-semibold">
                  Detail Data Mahasiswa per Program Studi dan Semester
                </h3>
                <p class="mt-1 text-sm text-gray-500">
                  Data menampilkan distribusi jumlah mahasiswa per semester
                  (Ganjil dan Genap) untuk setiap program studi dengan detail
                  angka dan persentase
                </p>
                <div class="flex items-center gap-2 mt-2">
                  <div class="flex items-center">
                    <span
                      class="inline-block w-3 h-3 mr-1 bg-blue-100 rounded-full"
                    ></span>
                    <span class="text-xs font-medium text-gray-600"
                      >Semester Ganjil: {{ totalGanjil.toLocaleString() }} ({{
                        ganjilPercent
                      }}%)</span
                    >
                  </div>
                  <div class="flex items-center">
                    <span
                      class="inline-block w-3 h-3 mr-1 bg-green-100 rounded-full"
                    ></span>
                    <span class="text-xs font-medium text-gray-600"
                      >Semester Genap: {{ totalGenap.toLocaleString() }} ({{
                        genapPercent
                      }}%)</span
                    >
                  </div>
                </div>
              </div>
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                      >
                        Program Studi
                      </th>
                      <th
                        class="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
                      >
                        Tahun Akademik
                      </th>
                      <th
                        class="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
                      >
                        <span class="text-blue-600">Semester Ganjil</span>
                      </th>
                      <th
                        class="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
                      >
                        <span class="text-green-600">Semester Genap</span>
                      </th>
                      <th
                        class="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
                      >
                        Total Mahasiswa
                      </th>
                      <th
                        class="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
                      >
                        Persentase
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr
                      v-for="item in processedChartData"
                      :key="item.kode_prodi"
                      class="hover:bg-gray-50"
                    >
                      <td
                        class="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap"
                      >
                        {{ formatProdiName(item) }}
                      </td>
                      <td
                        class="px-6 py-4 text-sm text-center text-gray-900 whitespace-nowrap"
                      >
                        {{ item.tahun_akademik || "N/A" }}
                      </td>
                      <td
                        class="px-6 py-4 text-sm text-center text-gray-900 whitespace-nowrap"
                      >
                        <span class="font-medium text-blue-800">
                          {{
                            (item.jumlah_mahasiswa_ganjil || 0).toLocaleString()
                          }}
                        </span>
                      </td>
                      <td
                        class="px-6 py-4 text-sm text-center text-gray-900 whitespace-nowrap"
                      >
                        <span class="font-medium text-green-800">
                          {{
                            (item.jumlah_mahasiswa_genap || 0).toLocaleString()
                          }}
                        </span>
                      </td>
                      <td
                        class="px-6 py-4 text-sm font-medium text-center text-gray-900 whitespace-nowrap"
                      >
                        {{ item.total_mahasiswa.toLocaleString() }}
                      </td>
                      <td
                        class="px-6 py-4 text-sm text-center text-gray-500 whitespace-nowrap"
                      >
                        {{ getPercentage(item.total_mahasiswa) }}%
                      </td>
                    </tr>
                  </tbody>
                  <!-- Summary row -->
                  <tfoot
                    v-if="processedChartData.length > 0"
                    class="bg-gray-100"
                  >
                    <!-- Total row -->
                    <tr
                      class="font-semibold bg-gray-100 border-b border-gray-300"
                    >
                      <td
                        class="px-6 py-4 text-sm font-semibold text-gray-900 whitespace-nowrap"
                      >
                        TOTAL KESELURUHAN
                      </td>
                      <td
                        class="px-6 py-4 text-sm font-semibold text-center text-gray-500 whitespace-nowrap"
                      >
                        -
                      </td>
                      <td
                        class="px-6 py-4 text-sm font-semibold text-center text-blue-800 whitespace-nowrap"
                      >
                        {{ totalGanjil.toLocaleString() }}
                      </td>
                      <td
                        class="px-6 py-4 text-sm font-semibold text-center text-green-800 whitespace-nowrap"
                      >
                        {{ totalGenap.toLocaleString() }}
                      </td>
                      <td
                        class="px-6 py-4 text-sm font-semibold text-center text-gray-900 whitespace-nowrap"
                      >
                        {{ totalMahasiswa.toLocaleString() }}
                      </td>
                      <td
                        class="px-6 py-4 text-sm font-semibold text-center text-gray-900 whitespace-nowrap"
                      >
                        100%
                      </td>
                    </tr>

                    <!-- Distribution by semester row with detail -->
                    <tr class="text-sm bg-blue-50">
                      <td
                        colspan="6"
                        class="px-6 py-3 text-xs font-medium tracking-wider text-center text-blue-800"
                      >
                        Distribusi Mahasiswa:
                        <span class="font-semibold text-blue-800"
                          >Ganjil: {{ totalGanjil.toLocaleString() }} ({{
                            ganjilPercent
                          }}%)</span
                        >
                        <span class="mx-2">•</span>
                        <span class="font-semibold text-green-800"
                          >Genap: {{ totalGenap.toLocaleString() }} ({{
                            genapPercent
                          }}%)</span
                        >
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <!-- Empty State -->
              <div
                v-if="processedChartData.length === 0"
                class="py-16 text-center"
              >
                <div class="relative w-24 h-24 mx-auto mb-4">
                  <!-- Bar chart icon with animation -->
                  <svg
                    class="w-24 h-24 mx-auto text-gray-300"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <rect
                      x="4"
                      y="12"
                      width="4"
                      height="8"
                      rx="1"
                      class="animate-pulse-slow"
                      fill="currentColor"
                    />
                    <rect
                      x="10"
                      y="8"
                      width="4"
                      height="12"
                      rx="1"
                      class="animate-pulse-slower"
                      fill="currentColor"
                    />
                    <rect
                      x="16"
                      y="4"
                      width="4"
                      height="16"
                      rx="1"
                      class="animate-pulse-slowest"
                      fill="currentColor"
                    />
                  </svg>
                  <!-- Search/filter icon overlay -->
                  <svg
                    class="absolute bottom-0 right-0 w-10 h-10 p-1 text-blue-500 bg-white rounded-full"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>

                <h3 class="mt-2 text-lg font-medium text-gray-900">
                  Tidak ada data mahasiswa
                </h3>
                <p class="max-w-md mx-auto mt-2 text-base text-gray-500">
                  Tidak ada data mahasiswa yang sesuai dengan filter yang
                  dipilih.
                </p>

                <div
                  class="max-w-sm p-6 mx-auto mt-6 border border-blue-100 rounded-lg shadow-sm bg-blue-50"
                >
                  <div class="flex items-center mb-3">
                    <svg
                      class="w-5 h-5 mr-2 text-blue-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    <p class="text-sm font-semibold text-blue-800">
                      Tips Pencarian Data
                    </p>
                  </div>

                  <ul class="space-y-2 text-sm text-left text-blue-700">
                    <li class="flex items-start">
                      <svg
                        class="w-4 h-4 mt-0.5 mr-2 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      <span>Pilih tahun akademik yang berbeda di filter</span>
                    </li>
                    <li class="flex items-start">
                      <svg
                        class="w-4 h-4 mt-0.5 mr-2 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      <span>Pilih semester "Ganjil" atau "Genap" saja</span>
                    </li>
                    <li class="flex items-start">
                      <svg
                        class="w-4 h-4 mt-0.5 mr-2 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      <span>Pilih program studi tertentu</span>
                    </li>
                    <li class="flex items-start">
                      <svg
                        class="w-4 h-4 mt-0.5 mr-2 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      <span
                        >Atau reset semua filter untuk melihat semua data</span
                      >
                    </li>
                  </ul>

                  <button
                    @click="resetFilters"
                    class="w-full px-4 py-2 mt-4 text-sm font-medium text-blue-700 transition-colors bg-blue-100 border border-blue-200 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Reset Semua Filter
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </template>
    <template #footer>
      <FooterFe />
    </template>
  </Container>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import Container from "@/Layouts/Container.vue";
import HeaderFe from "@/Components/HeaderFe.vue";
import FooterFe from "@/Components/FooterFe.vue";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

// Props
const props = defineProps({
  tahunAkademikList: Array,
  prodiList: Array,
  initialChartData: Array,
});

// Reactive data
const selectedTahunAkademik = ref("");
const selectedSemester = ref("");
const selectedProdi = ref("");
// Safely handle props.initialChartData which might be null or undefined
const chartData = ref(
  props.initialChartData && props.initialChartData.data
    ? props.initialChartData.data
    : []
);
const pieChart = ref(null);
const pieChartInstance = ref(null);
const semesterChart = ref(null);
const semesterChartInstance = ref(null);
const isLoading = ref(false);

// Computed properties
const processedChartData = computed(() => {
  if (!chartData.value || chartData.value.length === 0) return [];

  // First, fetch all unique prodi codes to ensure we include all program studies
  const uniqueProdiCodes = [
    ...new Set(chartData.value.map((item) => item.kode_prodi)),
  ];

  // Create results array
  const results = [];

  try {
    // For each unique prodi code
    uniqueProdiCodes.forEach((kode_prodi) => {
      // Filter items for this prodi
      const prodiItems = chartData.value.filter(
        (item) => item.kode_prodi === kode_prodi
      );

      if (prodiItems.length > 0) {
        // Get the study program info from the first item
        const studyProgram = prodiItems[0].study_program;

        // Initialize counters
        let ganjilCount = 0;
        let genapCount = 0;

        // Process each item for this prodi
        prodiItems.forEach((item) => {
          // Get the semester value and convert to a number for safety
          const mahasiswaCount = parseInt(item.total_mahasiswa || 0);

          // Add to the appropriate counter based on semester
          if (item.semester === "ganjil") {
            ganjilCount += mahasiswaCount;
          } else if (item.semester === "genap") {
            genapCount += mahasiswaCount;
          } else {
            // If semester is not specified but can be inferred from tahun_semester
            if (item.tahun_semester) {
              if (item.tahun_semester.toLowerCase().includes("ganjil")) {
                ganjilCount += mahasiswaCount;
              } else if (item.tahun_semester.toLowerCase().includes("genap")) {
                genapCount += mahasiswaCount;
              }
            }
            // If we can't determine the semester, we won't count this data
          }
        });

        // Calculate total after processing all items for this prodi
        const totalCount = ganjilCount + genapCount;

        // Get the most recent tahun_akademik for this prodi
        const latestTahunAkademik =
          prodiItems
            .filter((item) => item.tahun_akademik)
            .sort((a, b) => {
              // Sort by tahun_akademik (assuming format is like "2022/2023")
              return b.tahun_akademik.localeCompare(a.tahun_akademik);
            })[0]?.tahun_akademik || "N/A";

        // Add to results
        // Calculate counts for each semester and add consistent field names
        results.push({
          kode_prodi,
          study_program: studyProgram,
          // Primary fields used in tables and displays
          tahun_akademik: latestTahunAkademik,
          jumlah_mahasiswa_ganjil: ganjilCount,
          jumlah_mahasiswa_genap: genapCount,
          // These fields are for backward compatibility
          ganjil: ganjilCount,
          genap: genapCount,
          total_mahasiswa: totalCount,
        });
      }
    });

    // Sort results by total_mahasiswa in descending order
    results.sort((a, b) => b.total_mahasiswa - a.total_mahasiswa);

    return results;
  } catch (error) {
    console.error("Error processing chart data:", error);
    return [];
  }
});

const totalMahasiswa = computed(() => {
  // Count the total students directly from the chart data
  return chartData.value.reduce(
    (total, item) => total + item.total_mahasiswa,
    0
  );
});

const totalGanjil = computed(() => {
  if (!processedChartData.value || !Array.isArray(processedChartData.value))
    return 0;
  return processedChartData.value.reduce(
    (total, item) => total + (item.jumlah_mahasiswa_ganjil || 0),
    0
  );
});

const totalGenap = computed(() => {
  if (!processedChartData.value || !Array.isArray(processedChartData.value))
    return 0;
  return processedChartData.value.reduce(
    (total, item) => total + (item.jumlah_mahasiswa_genap || 0),
    0
  );
});

// Computed property to get the total semester distribution
const semesterDistribution = computed(() => {
  return [
    {
      name: "Semester Ganjil",
      count: totalGanjil,
      percentage: ganjilPercent,
      color: "bg-blue-500",
      textColor: "text-blue-800",
    },
    {
      name: "Semester Genap",
      count: totalGenap,
      percentage: genapPercent,
      color: "bg-green-500",
      textColor: "text-green-800",
    },
  ];
});

const totalProdi = computed(() => {
  return processedChartData.value.length;
});

const averagePerProdi = computed(() => {
  if (totalProdi.value === 0) return 0;
  return Math.round(totalMahasiswa.value / totalProdi.value);
});

const ganjilPercent = computed(() => {
  if (totalMahasiswa.value === 0) return 0;
  return ((totalGanjil.value / totalMahasiswa.value) * 100).toFixed(1);
});

const genapPercent = computed(() => {
  if (totalMahasiswa.value === 0) return 0;
  return ((totalGenap.value / totalMahasiswa.value) * 100).toFixed(1);
});

// Methods
const getPercentage = (value) => {
  if (totalMahasiswa.value === 0) return 0;
  return ((value / totalMahasiswa.value) * 100).toFixed(1);
};

const getProdiSemesterPercentage = (semesterValue, totalValue) => {
  if (!totalValue || totalValue === 0) return 0;
  return (((semesterValue || 0) / totalValue) * 100).toFixed(1);
};

// Helper to format prodi names with jenjang
const formatProdiName = (prodi) => {
  if (!prodi) return "Unknown Prodi";

  let name = "";
  if (prodi.study_program) {
    if (prodi.study_program.jenjang) {
      name += prodi.study_program.jenjang + " ";
    }
    name += prodi.study_program.name || "";
  } else {
    name = `Prodi ${prodi.kode_prodi}`;
  }

  return name;
};

const getSelectedProdiName = () => {
  if (!selectedProdi.value || !props.prodiList || !props.prodiList.data)
    return null;

  // Ensure consistent type comparison (convert both to numbers for comparison)
  const foundProdi = props.prodiList.data.find(
    (prodi) => Number(prodi.kode_prodi) === Number(selectedProdi.value)
  );

  // Return the name if found, or a fallback
  if (foundProdi) {
    if (foundProdi.studyProgram) {
      return foundProdi.studyProgram.name || `Prodi ${foundProdi.kode_prodi}`;
    }
    return `Prodi ${foundProdi.kode_prodi}`;
  }

  return null;
};

// Function to reset all filters
const resetFilters = () => {
  selectedTahunAkademik.value = "";
  selectedSemester.value = "";
  selectedProdi.value = "";
  updateChart();
};

const updateChart = async () => {
  try {
    isLoading.value = true;

    const params = new URLSearchParams();
    if (selectedTahunAkademik.value)
      params.append("tahun_akademik", selectedTahunAkademik.value);
    if (selectedSemester.value)
      params.append("semester", selectedSemester.value);
    if (selectedProdi.value) {
      // Always convert to integer if possible
      const prodiValue = Number.isNaN(Number(selectedProdi.value))
        ? selectedProdi.value
        : Number(selectedProdi.value);
      params.append("kode_prodi", prodiValue);
    }

    const url = `/chart-chart-api?${params.toString()}`;

    const response = await fetch(url);

    if (!response.ok) {
      console.error(`API Error: ${response.status}`, {
        url,
        params: Object.fromEntries(params),
        selectedProdi: selectedProdi.value,
        prodiType: typeof selectedProdi.value,
      });
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data || !data.data) {
      chartData.value = [];
    } else {
      // Get the full data for each item
      chartData.value = data.data || [];
    }

    await nextTick();
    renderCharts();
  } catch (error) {
    console.error("Error updating chart:", error);

    // Provide more specific error information for debugging
    const errorMessage = error.message || "Unknown error";
    const prodiInfo = selectedProdi.value
      ? `Prodi: ${selectedProdi.value} (${typeof selectedProdi.value})`
      : "No prodi selected";

    console.error(`Chart error details: ${errorMessage}, ${prodiInfo}`);

    // Show user-friendly error message with Toast notification if available
    // or fallback to alert for critical errors
    if (window.Toast) {
      window.Toast.error(
        `Terjadi kesalahan saat mengambil data. Silakan coba lagi.`
      );
    } else {
      alert(
        `Terjadi kesalahan saat mengambil data. Silakan coba lagi dengan filter yang berbeda.`
      );
    }

    // Reset chart data to empty if there's an error
    chartData.value = [];

    // Still render charts to show empty state
    await nextTick();
    renderCharts();
  } finally {
    isLoading.value = false;
  }
};

const renderCharts = () => {
  try {
    renderPieChart();
    renderSemesterChart();
    console.log("Charts rendered successfully");
  } catch (error) {
    console.error("Error rendering charts:", error);
  }
};

const renderPieChart = () => {
  if (!pieChart.value) {
    return;
  }

  if (pieChartInstance.value) {
    pieChartInstance.value.destroy();
  }

  const ctx = pieChart.value.getContext("2d");

  // Clear the canvas first
  ctx.clearRect(0, 0, pieChart.value.width, pieChart.value.height);

  // Handle empty data case
  if (processedChartData.value.length === 0) {
    // Draw a nice empty state with icon and text
    const centerX = pieChart.value.width / 2;
    const centerY = pieChart.value.height / 2;

    // Draw icon (simple chart icon)
    ctx.beginPath();
    ctx.moveTo(centerX - 30, centerY - 10);
    ctx.lineTo(centerX - 30, centerY + 30);
    ctx.lineTo(centerX - 10, centerY + 10);
    ctx.lineTo(centerX + 10, centerY + 20);
    ctx.lineTo(centerX + 30, centerY);
    ctx.strokeStyle = "#CBD5E1";
    ctx.lineWidth = 3;
    ctx.stroke();

    // Draw text
    ctx.font = "16px system-ui, -apple-system, sans-serif";
    ctx.fillStyle = "#64748B";
    ctx.textAlign = "center";
    ctx.fillText("Tidak ada data untuk ditampilkan", centerX, centerY + 60);
    ctx.font = "14px system-ui, -apple-system, sans-serif";
    ctx.fillText(
      "Coba ubah filter untuk melihat data lainnya",
      centerX,
      centerY + 85
    );
    return;
  }

  // For single prodi view, we might want to show data by semester or year instead
  let labels, data, colors;

  if (selectedProdi.value && processedChartData.value.length === 1) {
    // If single prodi is selected, show semester distribution
    labels = ["Semester Ganjil", "Semester Genap"];

    // Get semester data from the selected prodi
    const ganjilCount =
      processedChartData.value[0].jumlah_mahasiswa_ganjil || 0;
    const genapCount = processedChartData.value[0].jumlah_mahasiswa_genap || 0;

    data = [ganjilCount, genapCount];
    colors = ["#3B82F6", "#22C55E"]; // Blue for Ganjil, Green for Genap
  } else {
    // Multiple prodi view - show distribution by prodi
    labels = processedChartData.value.map((item) => {
      const prodiName = formatProdiName(item);
      // Truncate very long names for better display
      return prodiName.length > 25
        ? prodiName.substring(0, 22) + "..."
        : prodiName;
    });
    data = processedChartData.value.map((item) => item.total_mahasiswa);

    // Generate a visually pleasing color palette with sufficient contrast
    colors = [
      "#3B82F6", // Blue
      "#22C55E", // Green
      "#F59E0B", // Amber
      "#EC4899", // Pink
      "#8B5CF6", // Purple
      "#06B6D4", // Cyan
      "#EF4444", // Red
      "#10B981", // Emerald
      "#6366F1", // Indigo
      "#F97316", // Orange
      "#14B8A6", // Teal
      "#A855F7", // Violet
      "#84CC16", // Lime
      "#F472B6", // Rose
      "#EAB308", // Yellow
    ];

    // If we have more data points than colors, generate additional colors
    if (data.length > colors.length) {
      // Generate additional colors with slight variations
      for (let i = 0; i < data.length - colors.length; i++) {
        const baseColor = colors[i % colors.length];
        // Create a slightly different shade
        const r = parseInt(baseColor.slice(1, 3), 16);
        const g = parseInt(baseColor.slice(3, 5), 16);
        const b = parseInt(baseColor.slice(5, 7), 16);
        // Adjust brightness slightly
        const newColor = `#${Math.max(0, Math.min(255, r - 20))
          .toString(16)
          .padStart(2, "0")}${Math.max(0, Math.min(255, g - 20))
          .toString(16)
          .padStart(2, "0")}${Math.max(0, Math.min(255, b - 20))
          .toString(16)
          .padStart(2, "0")}`;
        colors.push(newColor);
      }
    }
  }

  pieChartInstance.value = new Chart(ctx, {
    type: "pie",
    data: {
      labels: labels,
      datasets: [
        {
          data: data,
          backgroundColor: colors.slice(0, data.length),
          borderColor: "#ffffff",
          borderWidth: 2,
          hoverOffset: 15,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            padding: 15,
            usePointStyle: true,
            font: {
              size: 11,
            },
            // Limit number of legends shown to prevent overflow
            filter: function (legendItem, chartData) {
              // Only show top N legend items if there are too many
              return chartData.labels.length <= 10 || legendItem.index < 10;
            },
          },
          title: {
            display: chartData.value.length > 10,
            text: "Menampilkan 10 data teratas",
            font: {
              size: 10,
              style: "italic",
            },
            padding: { top: 10 },
          },
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((context.raw / total) * 100).toFixed(1);
              return `${
                context.label
              }: ${context.raw.toLocaleString()} (${percentage}%)`;
            },
          },
          padding: 12,
          bodyFont: {
            size: 13,
          },
        },
      },
      animation: {
        animateRotate: true,
        animateScale: true,
      },
    },
  });
};

const renderSemesterChart = () => {
  console.log("Rendering Semester Chart", {
    chartElementExists: !!semesterChart.value,
    ganjilTotal: totalGanjil.value,
    genapTotal: totalGenap.value,
  });

  if (!semesterChart.value) {
    console.error("Semester chart element not found");
    return;
  }

  if (semesterChartInstance.value) {
    semesterChartInstance.value.destroy();
  }

  const ctx = semesterChart.value.getContext("2d");

  // Clear the canvas first
  ctx.clearRect(0, 0, semesterChart.value.width, semesterChart.value.height);

  // Get the total values from computed properties
  const ganjilTotal = totalGanjil.value || 0;
  const genapTotal = totalGenap.value || 0;

  // Handle empty data case
  if (ganjilTotal === 0 && genapTotal === 0) {
    // Draw a nice empty state with icon and text
    const centerX = semesterChart.value.width / 2;
    const centerY = semesterChart.value.height / 2;

    // Draw icon (simple bar chart icon)
    ctx.beginPath();
    ctx.rect(centerX - 35, centerY - 5, 15, 35);
    ctx.rect(centerX - 10, centerY - 20, 15, 50);
    ctx.rect(centerX + 15, centerY - 10, 15, 40);
    ctx.fillStyle = "#E2E8F0";
    ctx.fill();

    // Draw text
    ctx.font = "16px system-ui, -apple-system, sans-serif";
    ctx.fillStyle = "#64748B";
    ctx.textAlign = "center";
    ctx.fillText(
      "Tidak ada data semester untuk ditampilkan",
      centerX,
      centerY + 60
    );
    ctx.font = "14px system-ui, -apple-system, sans-serif";
    ctx.fillText("Pilih filter lain untuk melihat data", centerX, centerY + 85);
    return;
  }

  // Create interesting data visualization based on filter selections
  let chartType = "bar"; // Default to bar chart
  let labels = ["Semester Ganjil", "Semester Genap"]; // Default labels
  let datasets = [];

  console.log("Chart data preparation:", {
    ganjilTotal,
    genapTotal,
  });

  // If specific program study is selected, show breakdown by year if possible
  if (selectedProdi.value && chartData.value.length > 2) {
    // Group by tahun_akademik
    const yearData = {};
    chartData.value.forEach((item) => {
      if (item.tahun_akademik) {
        if (!yearData[item.tahun_akademik]) {
          yearData[item.tahun_akademik] = { ganjil: 0, genap: 0 };
        }

        if (item.semester === "ganjil") {
          yearData[item.tahun_akademik].ganjil += parseInt(
            item.total_mahasiswa || 0
          );
        } else if (item.semester === "genap") {
          yearData[item.tahun_akademik].genap += parseInt(
            item.total_mahasiswa || 0
          );
        }
      }
    });

    // Convert to chart format
    const years = Object.keys(yearData).sort();

    if (years.length > 1) {
      // Show trend over years
      chartType = "line";
      labels = years;
      datasets = [
        {
          label: "Semester Ganjil",
          data: years.map((year) => yearData[year].ganjil),
          borderColor: "#3B82F6",
          backgroundColor: "rgba(59, 130, 246, 0.2)",
          borderWidth: 2,
          pointBackgroundColor: "#3B82F6",
          fill: true,
          tension: 0.1,
        },
        {
          label: "Semester Genap",
          data: years.map((year) => yearData[year].genap),
          borderColor: "#22C55E",
          backgroundColor: "rgba(34, 197, 94, 0.2)",
          borderWidth: 2,
          pointBackgroundColor: "#22C55E",
          fill: true,
          tension: 0.1,
        },
      ];
    } else {
      // Default to standard semester comparison if not enough years
      chartType = "bar";
      labels = ["Semester Ganjil", "Semester Genap"];
      datasets = [
        {
          label: "Jumlah Mahasiswa",
          data: [ganjilTotal, genapTotal],
          backgroundColor: [
            "rgba(59, 130, 246, 0.8)",
            "rgba(34, 197, 94, 0.8)",
          ],
          borderColor: ["#3B82F6", "#22C55E"],
          borderWidth: 1,
          borderRadius: 5,
          hoverOffset: 10,
        },
      ];
    }
  } else {
    // Default view - simple ganjil/genap comparison
    chartType = "bar";
    labels = ["Semester Ganjil", "Semester Genap"];
    datasets = [
      {
        label: "Jumlah Mahasiswa",
        data: [ganjilTotal, genapTotal],
        backgroundColor: ["rgba(59, 130, 246, 0.8)", "rgba(34, 197, 94, 0.8)"],
        borderColor: ["#3B82F6", "#22C55E"],
        borderWidth: 1,
        borderRadius: 5,
        hoverOffset: 10,
      },
    ];
  }

  // Debug log right before creating the chart
  console.log("Creating semester chart with:", {
    chartType,
    labels,
    datasets,
    canvasElement: semesterChart.value,
  });

  semesterChartInstance.value = new Chart(ctx, {
    type: chartType,
    data: {
      labels: labels,
      datasets: datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: chartType === "line", // Show legend only for line charts
          position: "top",
          align: "center",
          labels: {
            usePointStyle: true,
            padding: 15,
            font: {
              size: 12,
            },
          },
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const value = context.raw;
              const percent = totalMahasiswa.value
                ? ((value / totalMahasiswa.value) * 100).toFixed(1)
                : 0;
              return `${
                context.dataset.label || ""
              }: ${value.toLocaleString()} mahasiswa (${percent}%)`;
            },
          },
          padding: 12,
          titleFont: {
            size: 13,
          },
          bodyFont: {
            size: 13,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: function (value) {
              return value.toLocaleString();
            },
            font: {
              size: 11,
            },
          },
          title: {
            display: true,
            text: "Jumlah Mahasiswa",
            font: {
              size: 12,
            },
          },
        },
        x: {
          ticks: {
            font: {
              size: 11,
            },
          },
        },
      },
      animation: {
        duration: 1000,
        easing: "easeOutQuart",
      },
    },
  });
};

// Lifecycle
onMounted(async () => {
  try {
    // Ensure chartData is properly initialized
    if (props.initialChartData && props.initialChartData.data) {
      chartData.value = props.initialChartData.data;
    } else {
      chartData.value = [];
    }

    // Wait for the DOM to update before rendering charts
    await nextTick();

    // Ensure chart elements are accessible
    console.log("Chart elements before rendering:", {
      pieChartExists: !!pieChart.value,
      semesterChartExists: !!semesterChart.value,
    });

    // Wait for chart elements to be mounted
    setTimeout(() => {
      renderCharts();

      // Add event listener for window resize
      const resizeHandler = () => {
        renderCharts();
      };
      window.addEventListener("resize", resizeHandler);

      // Setup cleanup on component unmount
      onUnmounted(() => {
        window.removeEventListener("resize", resizeHandler);

        // Clean up chart instances
        if (pieChartInstance.value) {
          pieChartInstance.value.destroy();
        }

        if (semesterChartInstance.value) {
          semesterChartInstance.value.destroy();
        }
      });
    }, 150);
  } catch (error) {
    console.error("Error during component initialization:", error);
    // Initialize with empty data if there's an error
    chartData.value = [];
    await nextTick();
    renderCharts();
  }
});
</script>

<style scoped>
/* Custom styles for consistent frontend layout */
.text-heading-5 {
  font-size: 1.5rem;
  line-height: 2rem;
}

.text-dark {
  color: #1f2937;
}

/* Custom animations for empty state */
.animate-pulse-slow {
  animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.animate-pulse-slower {
  animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  animation-delay: 0.5s;
}

.animate-pulse-slowest {
  animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  animation-delay: 1s;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
